<template>
  <section v-if="!SSR" class="container-login">
    <div class="bg-auth">
      <div class="d-flex justify-content-center align-items-center">
        <div class="card border-login d-flex">
          <div class="card-body">
            <div class="card-title text-center">
              <digitalife-logo data-cy="digitalife-logo" />
            </div>
            <div class="card-subtitle mt-5 mb-4" data-cy="login-text">
              Inicia sesión
            </div>
            <div class="card-text">
              <VeeForm :validation-schema="loginSchema" @submit="onLogin">
                <div>
                  <label for="email" />
                  <Field
                    id="email"
                    v-model="email"
                    class="input-field"
                    type="email"
                    name="email"
                    data-cy="email-field"
                    :placeholder="emailPlaceholderText"
                    @mouseover="onEmailMouseOver"
                    @mouseout="onEmailMouseOut"
                  />
                  <ErrorMessage
                    name="email"
                    class="error"
                    data-cy="email-error"
                  />
                </div>
                <div class="mb-3">
                  <label
                    for="password"
                    class="d-flex justify-content-end password-icon-see-hide"
                    data-cy="password-confirmation-required"
                  >
                    <view-hide-password
                      :show-password="showPassword"
                      @toggle="togglePasswordVisibility"
                    />
                  </label>
                  <div class="d-flex">
                    <Field
                      id="password"
                      v-model="password"
                      class="input-field"
                      :type="showPassword ? 'text' : 'password'"
                      name="password"
                      data-cy="password-field"
                      :minlenght="9"
                      :placeholder="passwordPlaceholderText"
                      @mouseover="onPasswordMouseOver"
                      @mouseout="onPasswordMouseOut"
                    />
                  </div>
                  <ErrorMessage
                    name="password"
                    class="error"
                    data-cy="password-error"
                  />
                </div>
                <div
                  v-if="loginErrors.length > 0"
                  class="text-center error"
                  data-cy="credentials-error"
                >
                  <ul class="p-0 list-unstyled">
                    <li
                      v-for="(error, index) in loginErrors"
                      :key="index"
                      class="text-danger text-start"
                    >
                      <i class="bi bi-exclamation-circle"></i>
                      {{ error.detail }}
                    </li>
                  </ul>
                </div>
                <div class="d-flex justify-content-end">
                  <div
                    class="text-remember-access pointer"
                    data-cy="remember-access"
                  >
                    <label
                      for="rememberAccess"
                      class="d-flex align-items-center"
                    >
                      <input
                        id="rememberAccess"
                        class="checkbox-input"
                        type="checkbox"
                        data-cy="remember-checkbox"
                      />
                      <span class="ps-2 pointer"> Recordar acceso </span>
                    </label>
                  </div>
                </div>

                <div class="d-flex justify-content-center mt-3">
                  <auth-button
                    :button-text="'Iniciar sesión'"
                    :is-submitting="isSubmitting"
                  />
                </div>
              </VeeForm>
              <div class="d-flex justify-content-between mt-3">
                <div class="text-reset-password">
                  <a @click="onRegister()"> Crear cuenta </a>
                </div>
                <div class="d-flex align-items-center" data-cy="reset-password">
                  <base-link
                    :to="'/contrasena/reestablecer'"
                    class="text-reset-password"
                  >
                    Restablecer contraseña
                  </base-link>
                </div>
              </div>
              <social-networks :option-text="'o inicia sesión con'" />
            </div>
          </div>
        </div>
      </div>
      <banner-login v-if="banner" :banner="banner" />
    </div>
  </section>
</template>

<script lang="ts">
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import AuthButton from "@/components/common/AuthButton.vue";
import DigitalifeLogo from "@/components/common/DigitalifeLogo.vue";
import SocialNetworks from "@/components/common/SocialNetworks.vue";
import ViewHidePassword from "@/components/common/ViewHidePassword.vue";
import BannerLogin from "@/components/auth/BannerLogin.vue";
import type ILogin from "~/interfaces/ILogin";
import BaseLink from "@/components/buttons/BaseLink.vue";

import { Jsona } from "jsona";
import type { IBanners } from "~/interfaces/IBanners";
import { AxiosError } from "axios";
import type { RouteLocationNormalized } from "vue-router";
import type { IUserLogged } from "~/interfaces/IUserLogged.js";
import googleAnalytics from "../services/GoogleAnalyticsService";
import auth from "../services/AuthService";
import seoService from "../services/SeoService";

const dataFormatter = new Jsona();

interface ErrorItem {
  source: {
    pointer: string;
  };
  title: string;
  detail: string;
}

export default {
  components: {
    AuthButton,
    BannerLogin,
    DigitalifeLogo,
    ErrorMessage,
    Field,
    SocialNetworks,
    ViewHidePassword,
    VeeForm,
    BaseLink,
  },
  setup() {
    definePageMeta({
      layout: "simple",
      middleware: "previous-route",
    });

    const previousRoute = useState("previousRoute");

    const loginSchema = {
      email(value: string) {
        const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
        if (!value) {
          return "Tu correo electrónico es necesario";
        } else if (!regex.test(value)) {
          return "Ingresa un correo electrónico válido";
        } else {
          return true;
        }
      },
      password(value: string) {
        if (!value) {
          return "Tu contraseña es necesaria";
        } else {
          return true;
        }
      },
    };

    return {
      loginSchema,
      previousRoute,
    };
  },
  data() {
    return {
      emailPlaceholderText: "Correo electrónico",
      passwordPlaceholderText: "Contraseña",
      loginErrors: [] as ErrorItem[],
      email: "",
      password: "",
      showPassword: false,
      banner: null as IBanners | null,
      isSubmitting: false,
      SSR: import.meta.env.SSR,
    };
  },
  created() {
    seoService.setData({
      title: "Inicio de sesión",
    });
  },
  mounted() {
    this.emailPlaceholderText = this.$t("Email");
    this.passwordPlaceholderText = this.$t("Password");
    auth.getLoginInitData().then((response) => {
      if (response) {
        this.banner = dataFormatter.deserialize(response) as IBanners;
      }
    });
  },
  methods: {
    async onLogin() {
      try {
        this.isSubmitting = true;
        const user: ILogin = { email: this.email, password: this.password };
        const response = await auth.login(user);
        const userData: IUserLogged = dataFormatter.deserialize(
          response,
        ) as IUserLogged;
        if (userData && userData.token) {
          this.isSubmitting = false;
          if (!response.authorized_device) {
            return this.$router.push("/verificacion-dos-pasos");
          }

          if (this.$route.redirectedFrom) {
            this.$router.push(this.$route.redirectedFrom.fullPath);
          } else {
            if (
              this.previousRoute &&
              ((this.previousRoute as RouteLocationNormalized)?.name ==
                "assembly-new" ||
                (this.previousRoute as RouteLocationNormalized)?.name ==
                  "assembly-edit") &&
              response.meta.assembly_config_slug
            ) {
              this.$router.push({
                name: "assembly-edit",
                params: { id: response.meta.assembly_config_slug },
              });
            } else {
              this.$router.back();
            }
          }
          googleAnalytics.login();
        } else if (response.errors) {
          this.loginErrors = response.errors;
          this.isSubmitting = false;
        } else {
          this.loginErrors = [
            {
              source: {
                pointer: "",
              },
              title: this.$t("An unexpected error occurred"),
              detail: this.$t("An unexpected error occurred"),
            },
          ];
          this.isSubmitting = false;
        }
      } catch (error) {
        this.isSubmitting = false;
        if (error instanceof AxiosError && error.response) {
          this.loginErrors = error.response.data.errors;
        } else {
          console.error("Error", error);
        }
      }
    },
    onEmailMouseOver() {
      this.emailPlaceholderText = "Escribe aquí tu correo electrónico";
    },
    onEmailMouseOut() {
      this.emailPlaceholderText = "Correo electrónico";
    },
    onPasswordMouseOver() {
      this.passwordPlaceholderText = "Escribe aquí tu contraseña";
    },
    onPasswordMouseOut() {
      this.passwordPlaceholderText = "Contraseña";
    },
    async onRegister() {
      this.$router.push("/registrarme");
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/auth";
</style>
